import React, { useState, useRef } from 'react';
import { Upload, Download, X, Loader2, AlertCircle, CheckCircle2, ShieldCheck, FileWarning } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Reusable Bulk Upload Modal with optional Validation Preview (dry-run) flow.
 *
 * Props:
 *  - open, onClose
 *  - title, description
 *  - templateUrl   — GET endpoint for CSV template (optional)
 *  - uploadUrl     — POST endpoint for the CSV
 *  - uploadParams  — extra query params (merged with headerControl selection)
 *  - templateFileName
 *  - columns       — array of column names shown as chips
 *  - onUploadSuccess
 *  - supportsDryRun — when true, shows a 2-step flow: "Validate" (dry_run=true) → "Commit".
 *  - headerControl — optional { label, options: [{value,label}], value, onChange, help? }
 *                    renders a required dropdown above the file picker whose `value` is added
 *                    to the POST query string. Used by CTC Bulk Upload to pick a salary structure
 *                    once for all rows (auto-mapped from the Masters → Salary Structures).
 */
const BulkUploadModal = ({
  open, onClose,
  title = 'Bulk Upload',
  description = 'Upload a CSV file with data',
  templateUrl, uploadUrl,
  uploadParams = {},
  templateFileName = 'template.csv',
  onUploadSuccess,
  columns = [],
  supportsDryRun = false,
  headerControl = null,
}) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(null); // 'validate' | 'commit'
  const [previewResult, setPreviewResult] = useState(null); // from dry-run
  const [finalResult, setFinalResult] = useState(null);     // from commit
  const fileRef = useRef(null);

  if (!open) return null;

  const result = finalResult || previewResult;
  const isCommitted = !!finalResult;
  const hasErrors = (result?.error_count || 0) > 0;

  const handleClose = () => {
    setFile(null);
    setPreviewResult(null);
    setFinalResult(null);
    onClose();
  };

  const resetPreviewOnFileChange = (f) => {
    setFile(f);
    setPreviewResult(null);
    setFinalResult(null);
  };

  const handleDownloadTemplate = async () => {
    try {
      const qs = new URLSearchParams(uploadParams).toString();
      const url = `${API_URL}${templateUrl}${qs ? '?' + qs : ''}`;
      const { data } = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = blobUrl; link.setAttribute('download', templateFileName);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Template downloaded');
    } catch (err) { toast.error('Failed to download template'); }
  };

  const doUpload = async (mode) => {
    if (!file) { toast.error('Select a CSV file first'); return; }
    if (headerControl && headerControl.required !== false && !headerControl.value) {
      toast.error(`Please select ${headerControl.label || 'the required option'} first`); return;
    }
    setBusy(mode);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const qsObj = { ...uploadParams };
      if (headerControl?.value) qsObj[headerControl.paramName || 'structure_code'] = headerControl.value;
      if (mode === 'validate') qsObj.dry_run = 'true';
      const qs = new URLSearchParams(qsObj).toString();
      const url = `${API_URL}${uploadUrl}${qs ? '?' + qs : ''}`;
      const { data } = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (mode === 'validate') {
        setPreviewResult(data); setFinalResult(null);
        if (data.blocking_error) {
          toast.error(data.blocking_error);
        } else if (data.error_count === 0) {
          toast.success(`All ${data.success_count} rows look good — click Commit to save.`);
        } else {
          toast.error(`${data.error_count} rows have issues. Fix them or download the error report.`);
        }
      } else {
        setFinalResult(data);
        if (data.blocking_error) toast.error(data.blocking_error);
        if (data.success_count > 0) toast.success(`${data.success_count} records saved`);
        if (data.error_count > 0) toast.error(`${data.error_count} rows failed`);
        if (onUploadSuccess && data.success_count > 0) onUploadSuccess(data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(null);
    }
  };

  const downloadErrorReport = () => {
    if (!result?.errors?.length) return;
    // Collect all unique column names seen in error data rows.
    const cols = new Set();
    result.errors.forEach(e => Object.keys(e.data || {}).forEach(k => cols.add(k)));
    const colList = Array.from(cols);
    const header = ['row', 'error', ...colList];
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [header.join(',')];
    result.errors.forEach(e => {
      const row = [e.row, e.error, ...colList.map(c => (e.data || {})[c] || '')];
      rows.push(row.map(esc).join(','));
    });
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = (templateFileName || 'upload') + '_errors.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Error report downloaded');
  };

  // Stage 73.7 — Universal "Download Result Report" CSV.
  // Works for every upload endpoint (employees, attendance, salary revision,
  // CTC bulk, biometric mapping, loans, statutory eligibility, etc.).
  //
  // Two source layouts are supported:
  //  - Modern (`row_results[]`): backend returns per-row outcomes with
  //    {row, status, employee_code, employee_id, ...domain fields, reason}.
  //    Used by /employees/bulk-upload (Stage 73.6).
  //  - Legacy: backend returns just {success_count, error_count, errors[]}.
  //    We synthesize the report from this: one row per error with status
  //    "❌ Error" + the specific reason, plus a summary line for the
  //    N successes (admin gets a clear "Success" count + concrete issue list).
  const downloadResultReport = () => {
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    let csvLines = [];
    if (result?.row_results?.length) {
      // Modern shape — union of keys across all rows so domain-specific
      // fields (employee_code / attendance_id / loan_id, …) flow through.
      const cols = new Set();
      result.row_results.forEach(r => Object.keys(r).forEach(k => cols.add(k)));
      const colList = Array.from(cols);
      // Map raw status → user-friendly remark. "Success" for the happy cases
      // (created, updated, skipped) so the admin sees a single positive word
      // alongside each created auto-code. Error rows keep their literal status
      // so failures stand out, with the `reason` column giving the specific
      // why ("matched by aadhaar_number", "first_name is required", …).
      const niceStatus = (s) => {
        if (!s) return '';
        if (s === 'created') return '✅ Success (created)';
        if (s === 'updated') return '✅ Success (updated)';
        if (s === 'skipped') return '⏭ Skipped (duplicate)';
        if (s === 'would_create') return '🔍 Preview — would create';
        if (s === 'would_update') return '🔍 Preview — would update';
        if (s === 'would_skip')   return '🔍 Preview — would skip';
        if (s === 'error')        return '❌ Error';
        return s;
      };
      const headerCols = ['Remark', ...colList.filter(c => c !== 'status')];
      csvLines.push(headerCols.join(','));
      result.row_results.forEach(r => {
        const remark = niceStatus(r.status);
        const cells = [remark, ...colList.filter(c => c !== 'status').map(c => r[c] ?? '')];
        csvLines.push(cells.map(esc).join(','));
      });
    } else {
      // Legacy shape — build a synthetic per-row report from `errors[]` and a
      // summary line for the successes. Admin still gets concrete "what went
      // wrong on which row" info, just without auto-codes (the legacy endpoint
      // didn't return them).
      const errCols = new Set();
      (result?.errors || []).forEach(e => Object.keys(e.data || {}).forEach(k => errCols.add(k)));
      const colList = Array.from(errCols);
      const header = ['Remark', 'Row', 'Specific Issue', ...colList];
      csvLines.push(header.join(','));
      (result?.errors || []).forEach(e => {
        const row = ['❌ Error', e.row, e.error, ...colList.map(c => (e.data || {})[c] || '')];
        csvLines.push(row.map(esc).join(','));
      });
      // Summary footer — gives the success count clearly so admin doesn't
      // have to do math from the result panel.
      const successCount = (result?.success_count || 0) - (result?.error_count || 0);
      const finalSuccess = successCount > 0 ? successCount : (result?.success_count || 0);
      csvLines.push('');
      csvLines.push(esc(`✅ Success: ${finalSuccess} rows processed without errors`));
    }
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = (templateFileName || 'upload').replace(/\.csv$/i, '') + `_result_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success('Result report downloaded');
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="bulk-upload-modal">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-[#0052FF]" />
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          </div>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600" data-testid="bulk-upload-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">{description}</p>
            {templateUrl && (
              <button onClick={handleDownloadTemplate} data-testid="bulk-upload-download-template-btn"
                className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1 shrink-0 ml-3">
                <Download className="w-3 h-3" /> Download Template
              </button>
            )}
          </div>

          {columns.length > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
              <p className="text-xs font-semibold text-slate-600 mb-1.5">Expected CSV Columns:</p>
              <div className="flex flex-wrap gap-1.5">
                {columns.map((col, i) => (
                  <span key={i} className="px-2 py-0.5 bg-white border border-slate-200 rounded text-xs text-slate-700 font-mono">{col}</span>
                ))}
              </div>
            </div>
          )}

          {headerControl && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="bulk-upload-header-control">
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                {headerControl.label} {headerControl.required !== false && <span className="text-red-500">*</span>}
              </label>
              <select
                value={headerControl.value || ''}
                onChange={(e) => headerControl.onChange(e.target.value)}
                data-testid="bulk-upload-header-select"
                className="w-full h-9 px-3 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Pick one —</option>
                {(headerControl.options || []).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {headerControl.help && (
                <p className="text-[11px] text-slate-600 mt-1.5">{headerControl.help}</p>
              )}
            </div>
          )}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#0052FF]/40 transition-colors">
            <input ref={fileRef} type="file" accept=".csv" onChange={e => resetPreviewOnFileChange(e.target.files[0])} className="hidden" data-testid="bulk-upload-file-input" />
            <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-600 mb-2">
              {file ? <span className="font-medium text-slate-900">{file.name}</span> : 'Click to select a CSV file'}
            </p>
            <button onClick={() => fileRef.current?.click()} data-testid="bulk-upload-select-file-btn"
              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-medium">
              {file ? 'Change File' : 'Select File'}
            </button>
          </div>

          {/* Blocking error — shown front-and-center when the backend rejects the whole upload */}
          {result?.blocking_error && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 flex items-start gap-3" data-testid="bulk-upload-blocking-error">
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-900">Cannot upload</p>
                <p className="text-xs text-red-800 mt-1">{result.blocking_error}</p>
              </div>
            </div>
          )}

          {/* Result area — shows preview or committed result */}
          {result && !result.blocking_error && (
            <div
              className={`p-3 rounded-md text-sm ${
                hasErrors
                  ? 'bg-amber-50 border border-amber-200 text-amber-800'
                  : isCommitted
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
              data-testid="bulk-upload-result"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  {hasErrors
                    ? <AlertCircle className="w-4 h-4" />
                    : isCommitted
                      ? <CheckCircle2 className="w-4 h-4" />
                      : <ShieldCheck className="w-4 h-4" />}
                  <p className="font-medium">
                    {isCommitted
                      ? hasErrors
                        ? `❌ ${result.error_count} rows failed · ✅ ${(result.success_count || 0) - (result.error_count || 0) > 0 ? (result.success_count - result.error_count) : 0} succeeded`
                        : `✅ Success — ${result.success_count} record${result.success_count === 1 ? '' : 's'} saved`
                      : hasErrors
                        ? `Preview: ${result.success_count} rows valid · ${result.error_count} rows have issues — see specific reasons below`
                        : `✅ Preview: all ${result.success_count} rows look good — click Commit to save.`}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  {/* Stage 73.7 — Universal Result Report.
                      Available for EVERY upload (employees, attendance, salary,
                      CTC, biometric mapping, etc.). Shows success/error remarks
                      per row + the specific reason on failures. */}
                  {(result.row_results?.length > 0 || result.errors?.length > 0 || (result.success_count || 0) > 0) && (
                    <button
                      onClick={downloadResultReport}
                      data-testid="bulk-upload-download-result-btn"
                      className="text-xs text-emerald-700 hover:text-emerald-900 underline flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> Download Result Report
                    </button>
                  )}
                  {result.errors?.length > 0 && (
                    <button
                      onClick={downloadErrorReport}
                      data-testid="bulk-upload-download-errors-btn"
                      className="text-xs text-amber-700 hover:text-amber-900 underline flex items-center gap-1"
                    >
                      <FileWarning className="w-3 h-3" /> Errors only
                    </button>
                  )}
                </div>
              </div>
              {/* Group identical errors so 5000 "Employee X not found" rows don't drown the user. */}
              {(() => {
                const groups = {};
                (result.errors || []).forEach(e => {
                  // Normalize by replacing specific codes/values in the message.
                  const key = (e.error || '').replace(/'[^']+'/g, "'X'");
                  if (!groups[key]) groups[key] = { count: 0, sampleRows: [] };
                  groups[key].count++;
                  if (groups[key].sampleRows.length < 3) groups[key].sampleRows.push(e.row);
                });
                const top = Object.entries(groups).sort((a, b) => b[1].count - a[1].count).slice(0, 3);
                if (top.length === 0) return null;
                return (
                  <div className="mt-2 space-y-1">
                    <p className="text-[10px] uppercase tracking-wide font-semibold opacity-70">Specific issues:</p>
                    {top.map(([key, info], i) => (
                      <p key={i} className="text-xs ml-6">
                        <span className="font-semibold">{info.count}× </span>{key}{' '}
                        <span className="text-slate-500">(e.g. rows {info.sampleRows.join(', ')})</span>
                      </p>
                    ))}
                  </div>
                );
              })()}
              {result.errors?.length > 10 && (
                <p className="text-xs mt-1 ml-6 italic text-slate-500">Download the Result Report above for all {result.errors.length} rows with specific reasons.</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-slate-200">
          <Button variant="outline" onClick={handleClose} data-testid="bulk-upload-cancel-btn">
            {isCommitted ? 'Close' : 'Cancel'}
          </Button>

          {/* Two-step flow: Validate → Commit. Collapses to single Upload when supportsDryRun=false. */}
          {supportsDryRun && !isCommitted && (
            <Button
              onClick={() => doUpload('validate')}
              disabled={busy !== null || !file}
              data-testid="bulk-upload-validate-btn"
              variant="outline"
              className="border-[#0052FF] text-[#0052FF] hover:bg-[#0052FF]/10"
            >
              {busy === 'validate' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
              Validate (Preview)
            </Button>
          )}

          {!isCommitted && (
            <Button
              onClick={() => doUpload('commit')}
              disabled={busy !== null || !file || (supportsDryRun && previewResult && hasErrors)}
              data-testid="bulk-upload-submit-btn"
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white"
              title={supportsDryRun && previewResult && hasErrors ? 'Fix the errors above (or re-upload) before committing.' : ''}
            >
              {busy === 'commit' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              {supportsDryRun && previewResult && !hasErrors ? 'Commit & Upload' : 'Upload'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
